import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useApp } from "@/context/AppContext";
import { api, apiError } from "@/lib/api";
import { HotelGuestPicker, defaultHotelGuests } from "@/components/HotelGuestPicker";
import { X, CreditCard, Wallet, Loader2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

// item: { type, item_id, title, amount, details }
export const BookingModal = ({ item, onClose }) => {
  const { t, money, user } = useApp();
  const nav = useNavigate();
  const [form, setForm] = useState({
    contact_name: user?.name || "",
    contact_phone: "",
    contact_email: user?.email || "",
    start_date: "",
    payment_method: "stripe",
  });
  const [loading, setLoading] = useState(false);
  const isHotel = item.type === "hotel";
  const [guests, setGuests] = useState(() => defaultHotelGuests(item.details));
  const [quote, setQuote] = useState(null);

  useEffect(() => {
    if (!isHotel) return;
    const details = { ...item.details, ...guests };
    const tmr = setTimeout(() => {
      api.post("/quotes", { type: "hotel", item_id: item.item_id, details })
        .then((r) => setQuote(r.data)).catch((err) => { setQuote(null); const m = apiError(err.response?.data?.detail); if (m && err.response?.status === 400) toast.error(m); });
    }, 250);
    return () => clearTimeout(tmr);
  }, [isHotel, guests, item.item_id, item.details]);

  const details = isHotel ? { ...item.details, ...guests } : (item.details || {});
  const total = isHotel ? quote?.amount : item.amount;

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.contact_name || !form.contact_phone) {
      toast.error(t.dir === "rtl" ? "يرجى إكمال البيانات" : "Please complete the fields");
      return;
    }
    if (isHotel && (!guests.check_in || !guests.check_out || !quote)) {
      toast.error(t.guests.datesRequired);
      return;
    }
    setLoading(true);
    try {
      const { data: booking } = await api.post("/bookings", {
        type: item.type, item_id: item.item_id, title: item.title, amount: item.amount,
        details, ...form, start_date: isHotel ? guests.check_in : form.start_date, origin_url: window.location.origin,
      });
      if (form.payment_method === "on_arrival") {
        toast.success(t.dir === "rtl" ? "تم تأكيد الحجز!" : "Booking confirmed!");
        nav(`/booking/${booking.ref}`);
        return;
      }
      const { data: co } = await api.post("/payments/checkout", {
        booking_ref: booking.ref, origin_url: window.location.origin,
      });
      window.location.href = co.checkout_url;
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || err.message);
      setLoading(false);
    }
  };

  const inputCls = "w-full px-4 py-3 rounded-xl border border-slate-200 bg-slate-50/60 focus:border-tigris focus:ring-2 focus:ring-tigris/20 outline-none transition text-sm";

  return (
    <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center p-0 sm:p-4">
      <div className="absolute inset-0 bg-tigris-dark/60 backdrop-blur-sm" onClick={onClose} />
      <div data-testid="booking-modal" className="relative bg-white w-full sm:max-w-lg rounded-t-3xl sm:rounded-3xl shadow-2xl max-h-[92vh] overflow-y-auto no-scrollbar">
        <div className="sticky top-0 bg-tigris text-white px-6 py-5 flex items-center justify-between rounded-t-3xl">
          <div>
            <h3 className="font-bold text-lg">{t.booking.title}</h3>
            <p className="text-white/80 text-xs mt-0.5 line-clamp-1">{item.title}</p>
          </div>
          <button onClick={onClose} data-testid="close-booking-modal" className="p-1.5 hover:bg-white/15 rounded-lg"><X size={20} /></button>
        </div>

        <form onSubmit={submit} className="p-6 space-y-4">
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1.5">{t.booking.contactName}</label>
            <input data-testid="booking-name" className={inputCls} value={form.contact_name} onChange={(e) => set("contact_name", e.target.value)} required />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className={isHotel ? "col-span-2" : ""}>
              <label className="block text-xs font-semibold text-slate-600 mb-1.5">{t.booking.phone}</label>
              <input data-testid="booking-phone" className={inputCls} dir="ltr" value={form.contact_phone} onChange={(e) => set("contact_phone", e.target.value)} placeholder="+964..." required />
            </div>
            {!isHotel && (
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1.5">{t.booking.startDate}</label>
                <input data-testid="booking-date" type="date" className={inputCls} value={form.start_date} onChange={(e) => set("start_date", e.target.value)} />
              </div>
            )}
          </div>
          {isHotel && <HotelGuestPicker value={guests} onChange={setGuests} />}
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1.5">{t.booking.email}</label>
            <input data-testid="booking-email" type="email" dir="ltr" className={inputCls} value={form.contact_email} onChange={(e) => set("contact_email", e.target.value)} />
          </div>

          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-2">{t.booking.payMethod}</label>
            <div className="grid grid-cols-2 gap-3">
              <button type="button" data-testid="pay-card" onClick={() => set("payment_method", "stripe")}
                className={`flex items-center gap-2 px-4 py-3 rounded-xl border-2 text-sm font-semibold transition ${form.payment_method === "stripe" ? "border-tigris bg-tigris/5 text-tigris" : "border-slate-200 text-slate-500"}`}>
                <CreditCard size={18} /> {t.booking.payCard}
              </button>
              <button type="button" data-testid="pay-arrival" onClick={() => set("payment_method", "on_arrival")}
                className={`flex items-center gap-2 px-4 py-3 rounded-xl border-2 text-sm font-semibold transition ${form.payment_method === "on_arrival" ? "border-gold bg-gold/5 text-gold" : "border-slate-200 text-slate-500"}`}>
                <Wallet size={18} /> {t.booking.payArrival}
              </button>
            </div>
          </div>

          <div className="bg-slate-50 rounded-xl px-4 py-3 space-y-1.5">
            {isHotel && quote?.details?.breakdown && (
              <div className="text-xs text-slate-500 space-y-1" data-testid="hotel-breakdown">
                <div className="flex justify-between"><span>{money(quote.details.unit_price)} × {quote.details.nights} {t.guests.nights} × {quote.details.rooms} {t.guests.rooms}</span><span>{money(quote.details.breakdown.base)}</span></div>
                {quote.details.breakdown.extra_adult_fee > 0 && <div className="flex justify-between"><span>{t.guests.extraAdults} ({quote.details.breakdown.extra_adults})</span><span>{money(quote.details.breakdown.extra_adult_fee)}</span></div>}
                {quote.details.breakdown.children_fee > 0 && <div className="flex justify-between"><span>{t.guests.children} ({quote.details.children})</span><span>{money(quote.details.breakdown.children_fee)}</span></div>}
              </div>
            )}
            <div className="flex items-center justify-between">
              <span className="text-sm font-semibold text-slate-600">{t.booking.total}</span>
              <span className="text-xl font-extrabold text-tigris" data-testid="booking-total">{total != null ? money(total) : "—"}</span>
            </div>
          </div>

          <button type="submit" disabled={loading || (isHotel && !quote)} data-testid="confirm-booking-btn"
            className="btn-gold w-full py-3.5 rounded-xl font-bold flex items-center justify-center gap-2 disabled:opacity-60">
            {loading ? <><Loader2 size={18} className="animate-spin" /> {t.booking.processing}</>
              : (form.payment_method === "stripe" ? t.booking.confirm : t.booking.confirmArrival)}
          </button>
          <p className="flex items-center justify-center gap-1.5 text-xs text-slate-400">
            <ShieldCheck size={14} className="text-tigris" /> {t.sections.trust3}
          </p>
        </form>
      </div>
    </div>
  );
};
